import React from 'react';
import { formatDate } from '../../utils/dateFormatter';
import { TEXT } from '../../config/listingPerformanceLabels';
import { Figure, FigureWithTrend, OccupancyCell, VerdictWithTag, CollectionStatus, Hint } from './parts';
import { MarketCell } from './MarketPrice';
import { MARKET } from '../../config/listingPerformanceLabels';

/**
 * «أداء الوحدات» — the Airbnb board: one row per unit, the sheet's columns in the sheet's order
 * (occupancy first — founder, 22 Sep). Built for a Monday glance:
 *   - columns are GROUPED (occupancy · the Airbnb funnel · rating and price) with a separator between
 *     groups only, so the eye reads a step at a time, not a wall of numbers;
 *   - headers are short; the full definition shows on hover, focus or a press (<Hint>);
 *   - the row's week is written only when it differs from the table's week (in «آخر أسبوع» a unit can
 *     still be on an older week — that is exactly when it should stand out);
 *   - the verdict chip's hover says which rule fired, with the unit's own numbers.
 * A row opens the unit's detail under the table. Below 768px each row is a card (CSS).
 *
 * The owner view arrives already cut by the server: no verdict, no band, no collection. This component
 * only stops drawing the columns the answer does not carry.
 *
 * «مقابل السوق» (staff, while the comparison is switched on — `marketShown`) sits beside «سعر الليلة» in the
 * rating-and-price group: the listed price against similar units, next to last week's booked price.
 */

/** The week most rows are on — the table's week. Equality only; no date arithmetic in the browser. */
const commonWeek = (rows) => {
  const counts = new Map();
  rows.forEach(r => { if (r.weekEnd) counts.set(r.weekEnd, (counts.get(r.weekEnd) || 0) + 1); });
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] || null;
};

/** A short header with its full definition as a hint. Focusable so the keyboard reaches the definition too. */
const Head = ({ label, help, className }) => (
  <th scope="col" className={className}>
    <Hint text={help} tap><span className="lp-hint" tabIndex={0}>{label}</span></Hint>
  </th>
);
const metricHead = (k, className) => <Head label={TEXT.short[k]} help={TEXT.full[k]} className={className} />;

const AirbnbBoard = ({ rows = [], ownerView, marketShown = false, selected, onSelect }) => {
  if (!rows.length) return <p className="lp-empty">{TEXT.noRows}</p>;
  const tableWeek = commonWeek(rows);
  const lead = ownerView ? 1 : 2;
  return (
    // Between phone (cards) and a wide desktop the table scrolls inside its own box, never the page.
    <div className="lp-scroll">
      <table className="lp-table lp-board">
        <thead>
          <tr className="lp-groups" aria-hidden="true">
            <th colSpan={lead} />
            {/* Occupancy is one column and already says so — no group label above it. */}
            <th className="lp-group-start" />
            <th colSpan={4} className="lp-group-start">{TEXT.groups.funnel}</th>
            <th colSpan={marketShown ? 3 : 2} className="lp-group-start">{TEXT.groups.quality}</th>
            {!ownerView && <th className="lp-group-start" />}
          </tr>
          <tr>
            <th scope="col">{TEXT.unit}</th>
            {!ownerView && <th scope="col">{TEXT.verdict}</th>}
            {/* All channels — the nights are shared, so a per-platform occupancy would mislead (founder, 22 Sep). */}
            <th scope="col" className="lp-group-start">{TEXT.occupancyAllPlatforms}</th>
            {metricHead('impressions', 'lp-group-start')}
            {metricHead('pageViews')}
            {metricHead('entryRate')}
            {metricHead('newBookings')}
            {metricHead('rating', 'lp-group-start')}
            {metricHead('bookedNightRate')}
            {marketShown && <Head label={MARKET.head} help={MARKET.headHelp} />}
            {!ownerView && <Head label={TEXT.collection} help={TEXT.collectionHelp} className="lp-group-start" />}
          </tr>
        </thead>
        <tbody>
          {rows.map(r => {
            const isSelected = selected === r.unitCode;
            const open = () => onSelect(isSelected ? null : r.unitCode);
            const ownWeek = r.weekEnd && r.weekEnd !== tableWeek;
            return (
              <tr
                key={r.unitCode}
                className={`lp-row${isSelected ? ' lp-row--selected' : ''}${r.verdict === 'NOT_ON_AIRBNB' ? ' lp-row--quiet' : ''}`}
                onClick={open}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
                tabIndex={0}
                aria-expanded={isSelected}
                aria-label={`${TEXT.openDetail} ${r.unitCode}`}
              >
                <th scope="row" className="lp-board__unit">
                  <span className="lp-unit-code">{r.unitCode}</span>
                  {ownWeek && <span className="lp-unit-week">{formatDate(r.weekEnd, { year: undefined, month: 'short' })}</span>}
                  {r.experimentStart && (
                    <Hint text={`${TEXT.experiment} ${formatDate(r.experimentStart)}`}>
                      <span className="lp-experiment">
                        <i className="bi bi-flask" aria-hidden="true" /> {formatDate(r.experimentStart, { year: undefined, month: 'short' })}
                      </span>
                    </Hint>
                  )}
                </th>
                {!ownerView && <td data-label={TEXT.verdict} className="lp-board__verdict"><VerdictWithTag code={r.verdict} inputs={r.verdictInputs} badgeLost={r.badgeLost} /></td>}
                <td data-label={TEXT.occupancy} className="lp-board__occ lp-group-start">
                  <OccupancyCell cell={ownerView ? { ...r.occupancy, target: null } : r.occupancy} />
                </td>
                <td data-label={TEXT.short.impressions} className="lp-group-start"><FigureWithTrend cell={r.impressions} /></td>
                <td data-label={TEXT.short.pageViews}><FigureWithTrend cell={r.pageViews} /></td>
                <td data-label={TEXT.short.entryRate}><FigureWithTrend cell={r.entryRate} kind="pct1" /></td>
                <td data-label={TEXT.short.newBookings}><Figure value={r.newBookings?.value ?? null} /></td>
                <td data-label={TEXT.short.rating} className="lp-group-start"><FigureWithTrend cell={r.rating} kind="rating" /></td>
                <td data-label={TEXT.short.bookedNightRate}><FigureWithTrend cell={r.bookedNightRate} kind="money" /></td>
                {marketShown && <td data-label={MARKET.head}><MarketCell cell={r.market} /></td>}
                {!ownerView && (
                  <td data-label={TEXT.collection} className="lp-board__collect lp-group-start"><CollectionStatus collection={r.collection} /></td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default AirbnbBoard;
